"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  LABEL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { DataInput } from "@/components/tools/research-design/data-input";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { downloadText } from "@/lib/tool-kit/download";
import {
  codeRange,
  type NumberFormatter,
} from "@/lib/tool-kit/research-design/format";
import {
  isNumericCell,
  labelsFor,
  type ParsedTable,
  parseTable,
  toCsv,
  toTsv,
} from "@/lib/tool-kit/research-design/matrix";
import {
  conversionTable,
  MAX_TABLE_ROWS,
  reverseColumns,
  reverseScore,
} from "@/lib/tool-kit/research-design/reverse-scoring";

/** English strings. A translated content file overrides these through `ui`. */
export const REVERSE_SCORING_CALCULATOR_STRINGS = {
  scaleLegend: "Your scale",
  minLabel: "Lowest possible score",
  maxLabel: "Highest possible score",
  presetsLabel: "Common scales",
  scaleInvalid: "Enter a highest score that is greater than the lowest score.",
  modeLegend: "Reverse",
  modeSingle: "One score",
  modeTable: "A pasted table",
  valueLabel: "Original score",
  valuePlaceholder: "2",
  valuePrompt: "Enter a score to see it reversed.",
  reversedScore: "Reversed score",
  working:
    "Reversed = (lowest + highest) − original = ({min} + {max}) − {value} = {result}",
  valueOutside:
    "{value} is outside the {min} to {max} scale, so it has no reversed value.",
  conversionHeading: "Conversion table for a {range} scale",
  colOriginal: "Original score",
  colReversed: "Reversed score",
  conversionTruncated: "Showing the first {count} whole-number points.",
  dataLabel: "Scores — one row per respondent, one column per item",
  dataHint:
    "Paste from a spreadsheet, or type numbers separated by commas or spaces. Only the columns you tick are changed; blanks and text pass through untouched.",
  dataPlaceholder: "ID\tQ1\tQ2\tQ3\nR01\t5\t1\t4\nR02\t4\t2\t4",
  loadExample: "Load example",
  clear: "Clear",
  exampleData:
    "ID\tQ1\tQ2\tQ3\tQ4\nR01\t5\t1\t4\t2\nR02\t4\t2\t4\t1\nR03\t2\t4\t3\t4\nR04\t5\t2\t5\t1\nR05\t3\t3\t3\t3\nR06\t4\t1\t5\t2",
  firstRowHeader: "First row holds column names",
  firstColumnIds: "First column holds respondent IDs",
  showParsed: "Show the table as read",
  previewNote: "Showing the first {shown} of {total} rows.",
  idHeader: "ID",
  readAs: "Read as {rows} × {columns}.",
  rowCount: "one: {count} row|other: {count} rows",
  columnCount: "one: {count} column|other: {count} columns",
  columnFallback: "Column {n}",
  columnsLegend: "Columns to reverse",
  markReversed: "Add _R to the names of reversed columns",
  reversedSuffix: "_R",
  results: "Results",
  tablePrompt:
    "Paste a table of scores, or load the example, to reverse whole columns at once.",
  pickColumns: "Tick the columns that hold negatively worded items.",
  reversedCount: "one: {count} score reversed.|other: {count} scores reversed.",
  outOfRangeNote:
    "one: {count} score outside the scale was left unchanged — check it.|other: {count} scores outside the scale were left unchanged — check them.",
  skippedNote:
    "one: {count} blank or non-numeric cell in a reversed column was left as it was.|other: {count} blank or non-numeric cells in reversed columns were left as they were.",
  outputHeading: "Reversed table",
  copyTable: "Copy for a spreadsheet",
  copied: "Copied!",
  downloadCsv: "Download CSV",
  csvFile: "reverse-scored.csv",
} as const;

type Strings = typeof REVERSE_SCORING_CALCULATOR_STRINGS;
type Mode = "single" | "table";

const PRESETS: readonly [number, number][] = [
  [1, 4],
  [1, 5],
  [1, 7],
  [0, 4],
  [0, 10],
  [1, 10],
];
const OUTPUT_PREVIEW_ROWS = 8;

function SingleScore({
  min,
  max,
  strings,
  fmt,
}: {
  min: number;
  max: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [text, setText] = useState("");
  const value = parseNumberInput(text);
  const inside = value !== null && value >= min && value <= max;
  return (
    <div className="space-y-4">
      <div className="max-w-xs">
        <TextField
          id="rs-value"
          label={strings.valueLabel}
          numeric
          onChange={setText}
          placeholder={strings.valuePlaceholder}
          value={text}
        />
      </div>
      <div aria-live="polite" className="space-y-3">
        {value === null ? <Notice>{strings.valuePrompt}</Notice> : null}
        {value !== null && !inside ? (
          <Notice>
            {t(strings.valueOutside, {
              value: fmt(value),
              min: fmt(min),
              max: fmt(max),
            })}
          </Notice>
        ) : null}
        {value !== null && inside ? (
          <div className={`${RESULT_CARD} space-y-3`}>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.reversedScore}
                value={fmt(reverseScore(value, min, max))}
              />
            </StatGrid>
            <p className="font-mono text-muted-foreground text-sm">
              {t(strings.working, {
                min: fmt(min),
                max: fmt(max),
                value: fmt(value),
                result: fmt(reverseScore(value, min, max)),
              })}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function outputRows(
  table: ParsedTable,
  names: readonly string[],
  body: readonly (readonly string[])[],
  idHeader: string
): string[][] {
  const head = [...(table.ids ? [table.idName ?? idHeader] : []), ...names];
  const rows = body.map((row, i) => [
    ...(table.ids ? [table.ids[i] ?? ""] : []),
    ...names.map((_, column) => row[column] ?? ""),
  ]);
  return [head, ...rows];
}

function TableMode({
  min,
  max,
  strings,
  fmt,
}: {
  min: number;
  max: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const locale = useToolLocale();
  const [raw, setRaw] = useState("");
  const [header, setHeader] = useState<boolean | null>(null);
  const [ids, setIds] = useState<boolean | null>(null);
  const [chosen, setChosen] = useState<number[]>([]);
  const [mark, setMark] = useState(true);
  const table = parseTable(raw, isNumericCell, {
    header: header ?? undefined,
    ids: ids ?? undefined,
  });
  const width = Math.max(0, ...table.body.map((row) => row.length));
  const names = labelsFor(table.columns, width, (n) =>
    t(strings.columnFallback, { n })
  );
  const picked = new Set(chosen.filter((column) => column < width));
  const result = reverseColumns(table.body, picked, min, max);
  const outNames = names.map((name, column) =>
    mark && picked.has(column) ? `${name}${strings.reversedSuffix}` : name
  );
  const output = outputRows(table, outNames, result.body, strings.idHeader);
  const ready = width > 0 && table.body.length > 0;

  function reset(text: string) {
    setRaw(text);
    setHeader(null);
    setIds(null);
    setChosen([]);
  }

  function note(template: string, count: number) {
    return count > 0 ? (
      <Notice>{tPlural(template, locale, count, { count: fmt(count) })}</Notice>
    ) : null;
  }

  return (
    <div className="space-y-6">
      <DataInput
        columnNames={names}
        example={strings.exampleData}
        fmt={fmt}
        id="rs-data"
        onChange={setRaw}
        onHeader={setHeader}
        onIds={setIds}
        onReset={reset}
        strings={strings}
        summary={t(strings.readAs, {
          rows: tPlural(strings.rowCount, locale, table.body.length, {
            count: fmt(table.body.length),
          }),
          columns: tPlural(strings.columnCount, locale, width, {
            count: fmt(width),
          }),
        })}
        table={table}
        value={raw}
      >
        {ready ? (
          <fieldset className="space-y-2">
            <legend className={LABEL}>{strings.columnsLegend}</legend>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {names.map((name, column) => (
                <Checkbox
                  checked={picked.has(column)}
                  key={name}
                  label={name}
                  onChange={(on) =>
                    setChosen((current) =>
                      on
                        ? [...current, column]
                        : current.filter((value) => value !== column)
                    )
                  }
                />
              ))}
            </div>
            <Checkbox
              checked={mark}
              label={strings.markReversed}
              onChange={setMark}
            />
          </fieldset>
        ) : null}
      </DataInput>

      <section aria-live="polite" className="space-y-4">
        <h3 className={WIDGET_H3}>{strings.outputHeading}</h3>
        {ready ? null : <Notice>{strings.tablePrompt}</Notice>}
        {ready && picked.size === 0 ? (
          <Notice>{strings.pickColumns}</Notice>
        ) : null}
        {ready && picked.size > 0 ? (
          <>
            <p className="text-foreground text-sm">
              {tPlural(strings.reversedCount, locale, result.reversed, {
                count: fmt(result.reversed),
              })}
            </p>
            {note(strings.outOfRangeNote, result.outOfRange)}
            {note(strings.skippedNote, result.skipped)}
            <DataTable
              head={output[0]}
              rows={output.slice(1, OUTPUT_PREVIEW_ROWS + 1)}
            />
            {table.body.length > OUTPUT_PREVIEW_ROWS ? (
              <p className={HINT}>
                {t(strings.previewNote, {
                  shown: fmt(OUTPUT_PREVIEW_ROWS),
                  total: fmt(table.body.length),
                })}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyTable}
                text={toTsv(table.layout.header ? output : output.slice(1))}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(
                    strings.csvFile,
                    toCsv(table.layout.header ? output : output.slice(1)),
                    "text/csv;charset=utf-8"
                  )
                }
                type="button"
              >
                {strings.downloadCsv}
              </button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}

export default function ReverseScoringCalculatorWidget() {
  const strings = useToolUi(REVERSE_SCORING_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [minText, setMinText] = useState("1");
  const [maxText, setMaxText] = useState("5");
  const [mode, setMode] = useState<Mode>("single");
  const min = parseNumberInput(minText);
  const max = parseNumberInput(maxText);
  const valid = min !== null && max !== null && max > min;
  const conversion = valid ? conversionTable(min, max) : [];
  const points = valid ? Math.floor(max - min + 1e-9) + 1 : 0;

  return (
    <>
      <section className="space-y-4">
        <fieldset className="space-y-3">
          <legend className={LABEL}>{strings.scaleLegend}</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="rs-min"
              label={strings.minLabel}
              numeric
              onChange={setMinText}
              value={minText}
            />
            <TextField
              id="rs-max"
              label={strings.maxLabel}
              numeric
              onChange={setMaxText}
              value={maxText}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={HINT}>{strings.presetsLabel}</span>
            {PRESETS.map(([low, high]) => (
              <button
                className={BUTTON_SMALL}
                key={`${low}-${high}`}
                onClick={() => {
                  setMinText(String(low));
                  setMaxText(String(high));
                }}
                type="button"
              >
                {codeRange(low, high)}
              </button>
            ))}
          </div>
        </fieldset>
        <PillGroup
          label={strings.modeLegend}
          name="rs-mode"
          onChange={setMode}
          options={[
            { value: "single", label: strings.modeSingle },
            { value: "table", label: strings.modeTable },
          ]}
          value={mode}
        />
      </section>

      <section className="space-y-6">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {valid ? null : <Notice>{strings.scaleInvalid}</Notice>}
        {valid && mode === "single" ? (
          <SingleScore fmt={fmt} max={max} min={min} strings={strings} />
        ) : null}
        {valid && mode === "table" ? (
          <TableMode fmt={fmt} max={max} min={min} strings={strings} />
        ) : null}
        {valid ? (
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>
              {t(strings.conversionHeading, {
                range: codeRange(min, max),
              })}
            </h3>
            <DataTable
              head={[strings.colOriginal, strings.colReversed]}
              rows={conversion.map((row) => [fmt(row.from), fmt(row.to)])}
            />
            {points > conversion.length ? (
              <p className={HINT}>
                {t(strings.conversionTruncated, { count: fmt(MAX_TABLE_ROWS) })}
              </p>
            ) : null}
          </div>
        ) : null}
      </section>
    </>
  );
}
